import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tab,
  Tabs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GavelOutlined as GavelIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getRegulationCategories,
  createRegulationCategory,
  updateRegulationCategory,
  deleteRegulationCategory,
  getRegulations,
  createRegulation,
  updateRegulation,
  deleteRegulation
} from '../services/api';
import PageHeader from '../components/common/PageHeader';
import DeleteConfirmDialog from '../components/common/DeleteConfirmDialog';


function RegulationsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openRegulationDialog, setOpenRegulationDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subCategories: [],
    title: '',
    category: '',
    subCategory: '',
    documentUrl: '',
    year: new Date().getFullYear(),
    order: 0,
  });
  const [newSubCategory, setNewSubCategory] = useState('');
  const [file, setFile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: '',
    id: null,
    name: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesData, regulationsData] = await Promise.all([
        getRegulationCategories(),
        getRegulations()
      ]);
      setCategories(categoriesData);
      setRegulations(regulationsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch regulations data');
    }
    setLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (type, item = null) => {
    if (type === 'category') {
      setOpenCategoryDialog(true);
    } else {
      setOpenRegulationDialog(true);
    }
    setSelectedItem(item);
    if (item) {
      if (type === 'category') {
        setFormData({
          name: item.name,
          subCategories: Array.isArray(item.subCategories) ? item.subCategories : [],
          order: item.order || 0
        });
      } else {
        setFormData({
          title: item.title,
          categoryId: item.categoryId,
          subCategory: item.subCategory,
          documentUrl: item.documentUrl,
          year: item.year,
          order: item.order || 0
        });
      }
    } else {
      if (type === 'category') {
        setFormData({
          name: '',
          subCategories: [],
          order: 0
        });
      } else {
        setFormData({
          title: '',
          categoryId: '',
          subCategory: '',
          documentUrl: '',
          year: new Date().getFullYear(),
          order: 0
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenCategoryDialog(false);
    setOpenRegulationDialog(false);
    setSelectedItem(null);
    setFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };


  const handleSubmit = async (type) => {
    try {
      if (type === 'category') {
        const categoryData = {
          name: formData.name,
          subCategories: formData.subCategories,
          order: formData.order
        };
        if (selectedItem) {
          await updateRegulationCategory(selectedItem.id, categoryData);
        } else {
          await createRegulationCategory(categoryData);
        }
      } else {
        const formDataObj = new FormData();
        Object.keys(formData).forEach(key => {
          formDataObj.append(key, formData[key]);
        });
        if (file) {
          formDataObj.append('document', file);
        }

        if (selectedItem) {
          await updateRegulation(selectedItem.id, formDataObj);
        } else {
          await createRegulation(formDataObj);
        }
      }

      fetchData();
      handleCloseDialog();
      toast.success(`${type === 'category' ? 'Category' : 'Regulation'} ${selectedItem ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || `Failed to ${selectedItem ? 'update' : 'create'} ${type}`);
    }
  };

  const handleDeleteClick = (type, item) => {
    setDeleteConfirm({
      open: true,
      type,
      id: item.id || item._id,
      name: type === 'categories' ? item.name : item.title
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteLoading(true);
    try {
      if (type === 'categories') {
        await deleteRegulationCategory(id);
      } else {
        await deleteRegulation(id);
      }
      toast.success(`${type === 'categories' ? 'Category' : 'Regulation'} deleted successfully`);
      fetchData();
      setDeleteConfirm({ open: false, type: '', id: null, name: '' });
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(`Failed to delete ${type === 'categories' ? 'category' : 'regulation'}`);
    } finally {
      setDeleteLoading(false);
    }
  };


  const tableHeaderStyle = {
    fontFamily: 'var(--primary-sans)',
    fontWeight: 600,
    color: 'var(--subtitle-text)',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    py: 1.5,
    borderBottom: '1px solid var(--border-light)'
  };

  const tableCellStyle = {
    py: 1.8,
    borderBottom: '1px solid var(--border-light)',
    color: 'var(--header-title)',
    fontFamily: 'var(--primary-sans)'
  };

  return (
    <Box className="premium-container">
      <PageHeader
        titleIcon={<GavelIcon />}
        title="Rules & Regulations"
        btnText={activeTab === 0 ? "ADD CATEGORY" : "ADD REGULATION"}
        subtitle="Manage and organize policy documents, legal guidelines, and regulatory frameworks."
        onBtnClick={() => activeTab === 0 ? handleOpenDialog('category') : handleOpenDialog('regulation')}
      />

      <Box className="premium-table-card">
        <Box sx={{ px: activeTab === 0 ? 0 : 0 }}>
          <Box sx={{ borderBottom: '1px solid var(--border-light)', bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minWidth: 120,
                  color: 'var(--subtitle-text)',
                  '&.Mui-selected': { color: 'var(--premium-dark)' }
                },
                '& .MuiTabs-indicator': { backgroundColor: 'var(--accent-gold)', height: 3 }
              }}
            >
              <Tab label="Categories" />
              <Tab label="Regulations" />
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: 'var(--premium-dark)' }} />
            </Box>
          ) : activeTab === 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeaderStyle, pl: 3 }}>Name</TableCell>
                  <TableCell sx={tableHeaderStyle}>Sub Categories</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Order</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, pr: 3 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id} sx={{ '&:hover': { bgcolor: 'rgba(249, 250, 251, 0.5)' } }}>
                    <TableCell sx={{ ...tableCellStyle, pl: 3, fontWeight: 600 }}>{category.name}</TableCell>
                    <TableCell sx={tableCellStyle}>
                      {Array.isArray(category.subCategories) ? category.subCategories.map(sub => sub.name).join(', ') : ''}
                    </TableCell>
                    <TableCell sx={tableCellStyle} align="center">{category.order}</TableCell>
                    <TableCell sx={{ ...tableCellStyle, pr: 3 }} align="right">
                      <IconButton
                        onClick={() => handleOpenDialog('category', category)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' } }}
                      >
                        <EditIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick('categories', category)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' }, ml: 1 }}
                      >
                        <DeleteIcon sx={{ fontSize: 20 }} />
                      </IconButton>

                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeaderStyle, pl: 3 }}>Title</TableCell>
                  <TableCell sx={tableHeaderStyle}>Category</TableCell>
                  <TableCell sx={tableHeaderStyle}>Sub Category</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Year</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, pr: 3 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regulations.map((regulation) => (
                  <TableRow key={regulation.id} sx={{ '&:hover': { bgcolor: 'rgba(249, 250, 251, 0.5)' } }}>
                    <TableCell sx={{ ...tableCellStyle, pl: 3, fontWeight: 600 }}>{regulation.title}</TableCell>
                    <TableCell sx={tableCellStyle}>{regulation.category?.name}</TableCell>
                    <TableCell sx={tableCellStyle}>{regulation.subCategory}</TableCell>
                    <TableCell sx={tableCellStyle} align="center">{regulation.year}</TableCell>
                    <TableCell sx={{ ...tableCellStyle, pr: 3 }} align="right">
                      <IconButton
                        onClick={() => handleOpenDialog('regulation', regulation)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' } }}
                      >
                        <EditIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick('regulations', regulation)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' }, ml: 1 }}
                      >
                        <DeleteIcon sx={{ fontSize: 20 }} />
                      </IconButton>

                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'var(--primary-sans)', fontWeight: 700 }}>
          {selectedItem ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Category Name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="order"
            label="Display Order"
            type="number"
            value={formData.order}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Sub Categories</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                placeholder="New sub category"
                size="small"
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => {
                  if (newSubCategory.trim()) {
                    setFormData(prev => ({
                      ...prev,
                      subCategories: [...prev.subCategories, { name: newSubCategory.trim() }]
                    }));
                    setNewSubCategory('');
                  }
                }}
                sx={{ bgcolor: 'var(--premium-dark)', '&:hover': { bgcolor: 'var(--header-title)' } }}
              >
                Add
              </Button>
            </Box>
            {Array.isArray(formData.subCategories) && formData.subCategories.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.subCategories.map((sub, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: 'rgba(51, 65, 85, 0.05)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '16px',
                      border: '1px solid rgba(51, 65, 85, 0.1)'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{sub.name}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          subCategories: prev.subCategories.filter((_, i) => i !== index)
                        }));
                      }}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--subtitle-text)', fontStyle: 'italic' }}>
                No subcategories added yet.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'var(--subtitle-text)' }}>Cancel</Button>
          <Button
            onClick={() => handleSubmit('category')}
            variant="contained"
            sx={{ bgcolor: 'var(--premium-dark)', px: 4, '&:hover': { bgcolor: 'var(--header-title)' } }}
          >
            {selectedItem ? 'UPDATE' : 'ADD'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regulation Dialog */}
      <Dialog open={openRegulationDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'var(--primary-sans)', fontWeight: 700 }}>
          {selectedItem ? 'Edit Regulation' : 'Add Regulation'}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="title"
            label="Regulation Title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              label="Category"
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Sub Category</InputLabel>
            <Select
              name="subCategory"
              value={formData.subCategory}
              onChange={handleInputChange}
              label="Sub Category"
            >
              {categories
                .find(cat => cat.id === formData.categoryId)
                ?.subCategories?.map((sub, index) => (
                  <MenuItem key={index} value={sub.name}>
                    {sub.name}
                  </MenuItem>
                )) || []}
            </Select>
          </FormControl>
          <TextField
            name="year"
            label="Year"
            type="number"
            value={formData.year}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="order"
            label="Display Order"
            type="number"
            value={formData.order}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <Box sx={{ mt: 3, p: 2, border: '1px dashed var(--border-light)', borderRadius: '8px', textAlign: 'center' }}>
            <input
              type="file"
              id="regulation-file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="regulation-file">
              <Button variant="outlined" component="span" sx={{ color: 'var(--premium-dark)', borderColor: 'var(--premium-dark)' }}>
                {file ? 'CHANGE DOCUMENT' : 'UPLOAD PDF DOCUMENT'}
              </Button>
            </label>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--accent-gold)', fontWeight: 600 }}>
                Selected: {file.name}
              </Typography>
            )}
            {formData.documentUrl && !file && (
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--subtitle-text)' }}>
                Current: {formData.documentUrl.split('/').pop()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'var(--subtitle-text)' }}>Cancel</Button>
          <Button
            onClick={() => handleSubmit('regulation')}
            variant="contained"
            sx={{ bgcolor: 'var(--premium-dark)', px: 4, '&:hover': { bgcolor: 'var(--header-title)' } }}
          >
            {selectedItem ? 'UPDATE' : 'ADD'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => !deleteLoading && setDeleteConfirm({ ...deleteConfirm, open: false })}
        onConfirm={handleConfirmDelete}
        itemName={deleteConfirm.name}
        loading={deleteLoading}
      />
    </Box>
  );
}

export default RegulationsPage;
